/**
 * AccessorialRatesEditor (A1) — the per-tariff access-fee rate table.
 *
 * Opened from a tariff row in Settings, because these rates ARE part of the
 * tariff: the same elevator costs different money under different tariffs.
 *
 * Two independent sets, chosen by the tab: what the CLIENT is charged and what
 * the CONTRACTOR is paid. A $150 client elevator fee and a $120 contractor
 * elevator fee are unrelated numbers and are never derived from one another.
 *
 * Each row's PRICING METHOD is a choice, not an assumption. Elevator is usually
 * flat and shuttle usually a percentage, but nothing here enforces that pairing —
 * Daryl can make stairs a flat fee or the elevator a percentage if the business
 * changes. The fields shown follow the method:
 *
 *   Flat        amount
 *   Per unit    rate, unit label, free allowance  (charge only the excess)
 *   Percentage  percentage, minimum              ("14% or $300, whichever is higher")
 *
 * A row is inactive until it is switched on, so a freshly-seeded tariff prices
 * nothing until someone has actually entered figures.
 */

import React, { useState } from 'react';
import { CRM } from '../lib/theme';
import { numberInput, STEP_PER_LB } from '../lib/numberInput';
import { useApi, useApiClient } from '../hooks/useApi';
import { Icons } from '../lib/icons';

const T = { ...CRM, mono: "'DM Mono', monospace" };

const inputStyle: React.CSSProperties = {
  background: 'var(--crm-bg)', border: `1px solid ${T.border}`, color: T.text,
  borderRadius: '6px', padding: '5px 8px', fontSize: '12px', width: '100%', outline: 'none',
};

const METHODS = [
  { v: 'flat', l: 'Flat' },
  { v: 'unit', l: 'Per unit' },
  { v: 'pct', l: 'Percentage' },
];

const LEG_ORDER = ['origin', 'destination'];
const ACC_ORDER = ['elevator', 'stairs', 'long_walk', 'shuttle'];

function num(v: any): number | null {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function RateRow({ row, onSaved }: { row: any; onSaved: () => void }) {
  const api = useApiClient();
  const [f, setF] = useState({ ...row });
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const set = (k: string, v: any) => { setF((p: any) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      await api.updateAccessorialRate(row.id, {
        label: f.label,
        pricing_method: f.pricing_method,
        amount: num(f.amount),
        rate: num(f.rate),
        unit_label: f.unit_label || null,
        free_units: num(f.free_units) ?? 0,
        percentage: num(f.percentage),
        minimum_amount: num(f.minimum_amount),
        active: !!f.active,
      });
      setDirty(false);
      onSaved();
    } catch (e: any) {
      window.alert(e?.message || 'Could not save rate');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap' }}>
      <button
        onClick={() => set('active', !f.active)}
        title={f.active ? 'Active — this fee auto-populates' : 'Inactive — nothing is charged'}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: f.active ? T.green : T.dim, flexShrink: 0, padding: 0 }}
      >
        {f.active ? <Icons.checkboxOn size={17} aria-hidden="true" /> : <Icons.checkboxOff size={17} aria-hidden="true" />}
      </button>

      <input style={{ ...inputStyle, flex: '1 1 170px', minWidth: '150px' }} value={f.label ?? ''} onChange={(e) => set('label', e.target.value)} />

      <select style={{ ...inputStyle, width: 'auto', flex: '0 0 auto' }} value={f.pricing_method ?? 'flat'} onChange={(e) => set('pricing_method', e.target.value)}>
        {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
      </select>

      {/* Fields follow the chosen method — nothing irrelevant is shown. */}
      {f.pricing_method === 'flat' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }}>
          <span style={{ fontSize: 11, color: T.muted }}>$</span>
          <input {...numberInput(1)} style={{ ...inputStyle, width: '82px' }} value={f.amount ?? ''} onChange={(e) => set('amount', e.target.value)} />
        </label>
      )}

      {f.pricing_method === 'unit' && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: T.muted }}>$/unit</span>
            <input {...numberInput(f.unit_label === 'lb' ? STEP_PER_LB : 1)} style={{ ...inputStyle, width: '78px' }} value={f.rate ?? ''} onChange={(e) => set('rate', e.target.value)} />
          </label>
          <input placeholder="unit" style={{ ...inputStyle, width: '66px' }} value={f.unit_label ?? ''} onChange={(e) => set('unit_label', e.target.value)} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Units charged at zero — 1 free flight, or the first 75 ft of walk">
            <span style={{ fontSize: 11, color: T.muted }}>free</span>
            <input {...numberInput(1)} style={{ ...inputStyle, width: '62px' }} value={f.free_units ?? 0} onChange={(e) => set('free_units', e.target.value)} />
          </label>
        </>
      )}

      {f.pricing_method === 'pct' && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Percentage of the transportation charge only — never the subtotal">
            <input {...numberInput(0.01)} style={{ ...inputStyle, width: '70px' }} value={f.percentage ?? ''} onChange={(e) => set('percentage', e.target.value)} />
            <span style={{ fontSize: 11, color: T.muted }}>%</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Whichever is higher — the percentage or this minimum">
            <span style={{ fontSize: 11, color: T.muted }}>min $</span>
            <input {...numberInput(1)} style={{ ...inputStyle, width: '78px' }} value={f.minimum_amount ?? ''} onChange={(e) => set('minimum_amount', e.target.value)} />
          </label>
        </>
      )}

      <button
        onClick={save} disabled={busy || !dirty}
        style={{ marginLeft: 'auto', flex: '0 0 auto', background: dirty ? T.accentFill : 'transparent', color: dirty ? 'var(--crm-on-fill)' : T.dim,
                 border: `1px solid ${dirty ? T.accentLine : T.border}`, borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                 cursor: dirty ? 'pointer' : 'default' }}
      >
        {busy ? 'Saving…' : dirty ? 'Save' : 'Saved'}
      </button>
    </div>
  );
}

export function AccessorialRatesEditor({ tariff, onClose }: { tariff: any; onClose: () => void }) {
  const api = useApiClient();
  const [payer, setPayer] = useState<'client' | 'contractor'>('client');
  const { data: rates, refetch } = useApi<any[]>(
    async () => (await api.getAccessorialRates(tariff.id)).data || [],
    { dependencies: [tariff.id] }
  );

  const rows = (rates ?? [])
    .filter((r) => r.payer === payer)
    .sort((a, b) => ACC_ORDER.indexOf(a.accessorial) - ACC_ORDER.indexOf(b.accessorial) || LEG_ORDER.indexOf(a.leg) - LEG_ORDER.indexOf(b.leg));

  const tab = (v: 'client' | 'contractor', label: string) => (
    <button
      onClick={() => setPayer(v)}
      style={{ background: payer === v ? T.panel2 : 'transparent', color: payer === v ? T.text : T.muted,
               border: `1px solid ${payer === v ? T.border : 'transparent'}`, borderRadius: 6, padding: '5px 14px',
               fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 10, width: 'min(940px, 100%)', maxHeight: '86vh', overflow: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Access fees — {tariff.name}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer' }}><Icons.close size={18} aria-hidden="true" /></button>
        </div>
        <div style={{ fontSize: 11, color: T.muted, marginBottom: 14 }}>
          Long distance only. An active fee is added to the estimate automatically when the matching access box is
          checked, and stays editable and removable there.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {tab('client', 'Client charges')}
          {tab('contractor', 'Contractor pay')}
        </div>

        {rows.length === 0 && <div style={{ fontSize: 12, color: T.dim, padding: '10px 0' }}>No rates yet.</div>}
        {rows.map((r) => <RateRow key={r.id} row={r} onSaved={refetch} />)}

        <div style={{ fontSize: 11, color: T.dim, marginTop: 12, lineHeight: 1.6 }}>
          A percentage is always a percentage of the transportation charge, never of the subtotal. A per-unit free
          allowance charges only the excess — 3 flights with 1 free charges 2.
        </div>
      </div>
    </div>
  );
}
